import React, { useState, useEffect, useRef } from 'react';
import { api, useAuth } from '../App';
import { MessageCircle, Send, Plus, X, Clock, CheckCircle, AlertCircle, User, Shield } from 'lucide-react';

export default function SupportChat() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const chatEndRef = useRef(null);
  const isStaff = user?.role === 'owner' || user?.role === 'panzix_employee';

  const loadConversations = React.useCallback(async () => {
    try {
      const res = await api.get('/api/support/conversations');
      setConversations(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadMessages = React.useCallback(async (convId) => {
    try {
      const res = await api.get(`/api/support/messages/${convId}`);
      setMessages(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { if (selectedConv) loadMessages(selectedConv); }, [selectedConv, loadMessages]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedConv) loadMessages(selectedConv);
      loadConversations();
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedConv, loadConversations, loadMessages]);

  const handleSend = async () => {
    if (!input.trim() || !selectedConv || sending) return;
    const msg = input.trim();
    setInput('');
    setSending(true);
    try {
      await api.post('/api/support/messages', { conversation_id: selectedConv, content: msg });
      await loadMessages(selectedConv);
      loadConversations();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleCreateTicket = async () => {
    if (!newSubject.trim() || !newMessage.trim()) return;
    try {
      const res = await api.post('/api/support/conversations', { subject: newSubject, message: newMessage, priority: newPriority });
      setShowNewTicket(false);
      setNewSubject(''); setNewMessage(''); setNewPriority('normal');
      await loadConversations();
      setSelectedConv(res.data.id);
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async (convId, status) => {
    try {
      await api.patch(`/api/support/conversations/${convId}`, { status });
      loadConversations();
    } catch (err) { console.error(err); }
  };

  const getStatusIcon = (status) => {
    if (status === 'open') return <AlertCircle size={14} color="#f59e0b" />;
    if (status === 'resolved') return <CheckCircle size={14} color="#22c55e" />;
    return <Clock size={14} color="#64748b" />;
  };

  const getPriorityColor = (p) => {
    if (p === 'high') return '#dc2626';
    if (p === 'urgent') return '#7c2d12';
    return '#64748b';
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in" style={{ height: 'calc(100vh - 140px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>{isStaff ? 'Support Inbox' : 'Help & Support'}</h3>
          <p style={{ fontSize: 13, color: '#64748b' }}>{isStaff ? 'Manage and respond to subscriber support requests.' : 'Chat with our support team for assistance.'}</p>
        </div>
        {!isStaff && (
          <button className="btn-action" onClick={() => setShowNewTicket(true)} data-testid="new-ticket-btn">
            <Plus size={16} style={{ marginRight: 6 }} />New Conversation
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16, height: 'calc(100% - 60px)' }}>
        {/* Conversation List */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 14 }}>
            Conversations ({conversations.length})
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {conversations.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                {isStaff ? 'No support requests yet.' : 'No conversations yet. Start one!'}
              </div>
            ) : (
              conversations.map(c => (
                <div key={c.id} onClick={() => setSelectedConv(c.id)} data-testid={`conv-${c.id}`}
                  style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: selectedConv === c.id ? '#f0fdf4' : 'white', transition: 'background 0.15s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {getStatusIcon(c.status)}
                    <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{c.subject}</span>
                    <span style={{ fontSize: 10, color: getPriorityColor(c.priority), fontWeight: 600, textTransform: 'uppercase' }}>{c.priority}</span>
                  </div>
                  {isStaff && <p style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{c.company_name} • {c.created_by_name}</p>}
                  <p style={{ fontSize: 12, color: '#94a3b8' }}>
                    {c.last_message?.content?.slice(0, 60) || 'No messages'}{c.last_message?.content?.length > 60 ? '...' : ''}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 10, color: '#cbd5e1' }}>{c.message_count} messages</span>
                    <span style={{ fontSize: 10, color: '#cbd5e1' }}>{new Date(c.updated_at).toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedConv ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8' }}>
              <MessageCircle size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 14 }}>Select a conversation to view messages</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h4 style={{ fontSize: 15, fontWeight: 700 }}>{conversations.find(c => c.id === selectedConv)?.subject}</h4>
                  <p style={{ fontSize: 11, color: '#64748b' }}>
                    {conversations.find(c => c.id === selectedConv)?.created_by_name}
                    {isStaff && ` • ${conversations.find(c => c.id === selectedConv)?.company_name || ''}`}
                  </p>
                </div>
                {isStaff && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select value={conversations.find(c => c.id === selectedConv)?.status || 'open'}
                      onChange={e => handleStatusChange(selectedConv, e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} data-testid="status-select">
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="support-messages">
                {messages.map((msg, i) => {
                  const isMe = msg.sender_id === user?.id;
                  const isPanzix = msg.sender_role === 'owner' || msg.sender_role === 'panzix_employee';
                  return (
                    <div key={msg.id || i} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {isPanzix && <Shield size={10} color="#7B1FA2" />}
                        <span style={{ fontWeight: 600 }}>{msg.sender_name}</span>
                        {isPanzix && <span style={{ color: '#7B1FA2', fontSize: 10 }}>(Panzix Support)</span>}
                      </div>
                      <div style={{ padding: '12px 16px', borderRadius: 14, fontSize: 14, lineHeight: 1.5,
                        background: isMe ? (isPanzix ? 'linear-gradient(135deg, #7B1FA2, #4A148C)' : 'linear-gradient(135deg, #2E7D32, #1B5E20)') : '#f8fafc',
                        color: isMe ? 'white' : '#1e293b',
                        border: isMe ? 'none' : '1px solid #e2e8f0',
                        borderBottomRightRadius: isMe ? 4 : 14,
                        borderBottomLeftRadius: isMe ? 14 : 4,
                      }}>
                        {msg.content}
                      </div>
                      <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 2, textAlign: isMe ? 'right' : 'left' }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="chat-input-area">
                <input data-testid="support-input" value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder="Type your message..." disabled={sending} />
                <button onClick={handleSend} disabled={sending || !input.trim()} data-testid="support-send">
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowNewTicket(false)}>
          <div className="task-modal slide-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>New Support Conversation</h3>
              <button onClick={() => setShowNewTicket(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input data-testid="ticket-subject" value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="What do you need help with?" />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select data-testid="ticket-priority" value={newPriority} onChange={e => setNewPriority(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea data-testid="ticket-message" value={newMessage} onChange={e => setNewMessage(e.target.value)}
                placeholder="Describe your issue or question..." style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <button className="btn-action" onClick={handleCreateTicket} disabled={!newSubject.trim() || !newMessage.trim()}
              style={{ width: '100%' }} data-testid="submit-ticket">
              Start Conversation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
